/**
 * SmartImprove Playbook (placeholder)
 * -----------------------------------------------------------------------------
 * Phase-1 placeholder following the standard playbook template (SmartEvaluate /
 * SmartBid). This is the consultant METHODOLOGY document for SmartImprove —
 * distinct from the Improvement Knowledge Library (templates/checklists/articles).
 * Content only; the full methodology will be added in a later phase.
 */

const VALID = ["1", "2", "3", "4", "5"];

export default function SmartImprovePlaybookPage() {
  return (
    <div data-testid="smartimprove-playbook-page" className="max-w-4xl">
      <div className="mb-6">
        <p className="text-[10px] uppercase tracking-wider text-[#025979] font-semibold mb-1">
          SmartImprove Playbook
        </p>
        <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo" }}>
          Operational Improvement &amp; Optimization Guidance
        </h1>
        <p className="text-xs text-[#6B7280] mt-1 leading-relaxed max-w-3xl">
          How to use SmartImprove throughout warehouse optimization engagements,
          operational assessments, issue resolution programs, KPI improvement
          initiatives, and continuous improvement workshops.
        </p>
      </div>

      <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-sm px-4 py-3 mb-6 text-[12px] text-[#1E3A8A] leading-relaxed" data-testid="smartimprove-playbook-placeholder">
        This page will later contain the full SmartImprove consulting methodology.
        Operational standards, templates, checklists, and guidance articles live
        in the <strong>Improvement Knowledge Library</strong>.
      </div>

      {/* Engagement flow */}
      <div className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm p-3 mb-6" data-testid="smartimprove-playbook-flow">
        <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mb-2">Engagement flow</p>
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-[#111827]">
          <Pill>Assess</Pill><Arrow />
          <Pill>Diagnose</Pill><Arrow />
          <Pill>Prioritize</Pill><Arrow />
          <Pill>Improve</Pill><Arrow />
          <Pill>Measure</Pill>
        </div>
      </div>

      <Section n="1" title="Purpose of SmartImprove">
        <Bullets items={[
          "SmartImprove is a consultant-led operational improvement and optimization solution.",
          "Use it to assess current operations, diagnose issues, and prioritise improvement opportunities.",
          "Support continuous improvement programs grounded in operational data and KPIs.",
        ]} />
      </Section>

      <Section n="2" title="Operational Assessment">
        <Bullets items={[
          "Capture the current operating baseline across the relevant business processes.",
          "Use issue and KPI templates to structure the assessment consistently.",
          "Validate findings with operational stakeholders before drawing conclusions.",
        ]} />
      </Section>

      <Section n="3" title="Issue Resolution & Prioritization">
        <Bullets items={[
          "Log issues against operational areas with clear impact and root-cause notes.",
          "Prioritise improvements by value, effort, and operational risk.",
          "Sequence quick wins alongside structural improvements.",
        ]} />
      </Section>

      <Section n="4" title="KPI Improvement & Measurement">
        <Bullets items={[
          "Establish KPI baselines before changes so improvement is measurable.",
          "Track movement against baselines through the improvement program.",
          "Communicate progress in business terms tied to operational outcomes.",
        ]} />
      </Section>

      <Section n="5" title="Continuous Improvement Workshops">
        <Bullets items={[
          "Run structured workshops to surface and prioritise improvement ideas.",
          "Use the Improvement Knowledge Library for checklists and workshop kits.",
          "Close the loop by measuring impact and feeding lessons back into the program.",
        ]} />
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Primitives (shared playbook template)
// ---------------------------------------------------------------------------
function Section({ n, title, children }) {
  if (!VALID.includes(n)) n = "•";
  return (
    <section
      data-testid={`smartimprove-playbook-section-${n}`}
      className="mb-5 bg-white border border-[#E5E7EB] rounded-sm shadow-sm"
    >
      <div className="flex items-center gap-2 px-5 pt-4 pb-3 border-b border-[#E5E7EB] bg-[#F9FAFB]">
        <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-sm bg-[#025979] text-white text-[10px] font-bold">
          {n}
        </span>
        <h2
          className="text-sm font-semibold text-[#111827] tracking-tight"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          {title}
        </h2>
      </div>
      <div className="px-5 py-4 space-y-3">{children}</div>
    </section>
  );
}

function Bullets({ items }) {
  return (
    <ul className="space-y-2">
      {items.map((t, i) => (
        <li key={i} className="flex items-start gap-2 text-[12px] text-[#1F2937] leading-relaxed">
          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#025979] shrink-0" />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

function Pill({ children }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-sm bg-[#E0F2FE] text-[#025979] border border-[#025979]/20 font-medium">
      {children}
    </span>
  );
}

function Arrow() {
  return <span className="text-[#9CA3AF]">→</span>;
}
